import { Dispatch, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash';

import { PatronSessionState } from '@app/components';
import { queryKeys } from '@app/hooks';
import { axios, makeRequest } from '@app/utils';
import { UserSession } from '@app/constants';

import { PatronSession, useLogin, UserMetadata } from './useLogin';

interface SessionValidation {
  userSessionKey: UserSession;
  isConsortiaLogin?: boolean;
  setIsOkapiSessionDown: Dispatch<boolean>;
  setPatronSession?: Dispatch<PatronSessionState>;
}

interface ValidatedSession {
  isLoggedIn: boolean;
  session: PatronSession | null;
  validateToken: ({ token, redirectRoute }) => void;
}

interface PatronInfoDto {
  localPatronId: string[];
}

export const getOkapiSessionFromStorage = async (
  key: UserSession
): Promise<PatronSession | null> =>
  JSON.parse(sessionStorage.getItem(key) || 'null');

export const useSessionValidation = ({
  userSessionKey,
  isConsortiaLogin = false,
  setIsOkapiSessionDown,
  setPatronSession,
}: SessionValidation): ValidatedSession => {
  const [session, setSession] = useState<PatronSession | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [shouldGetGuestToken, setShouldGetGuestToken] = useState(false);
  const [isUserValidation, setIsUserValidation] = useState(false);
  const [SSORedirectRoute, setSSORedirectRoute] = useState();
  const { processPatronSession } = useLogin({
    isConsortiaLogin,
    setPatronSession,
  });
  const isGuestSession = userSessionKey === UserSession.guest;

  const validateToken = ({ token, redirectRoute }) => {
    setIsUserValidation(true);
    setSSORedirectRoute(redirectRoute);
    setSession({ token });
  };

  const handleValidationError = error => {
    if ([401, 403, 404].includes(error.request.status)) {
      sessionStorage.removeItem(userSessionKey);

      if (isGuestSession) {
        setShouldGetGuestToken(true);
      }
    } else {
      setIsOkapiSessionDown(true);
    }
  };

  useQuery(
    [queryKeys.validateUser, session],
    () => {
      const patronSession = session as PatronSession;

      return makeRequest<UserMetadata>({
        url: '/bl-users/_self',
        headers: {
          'X-Okapi-Token': patronSession.token,
        },
      })
        .then(userMetadata => {
          if (isUserValidation) {
            processPatronSession(
              patronSession.token,
              userMetadata,
              SSORedirectRoute
            );
          }

          setIsLoggedIn(true);

          return null;
        })
        .catch(error => {
          handleValidationError(error);

          return Promise.reject(error);
        });
    },
    {
      enabled: Boolean(session) && !isConsortiaLogin,
    }
  );

  useQuery(
    ['validateConsortiaUser', session],
    () => {
      const patronSession = session as PatronSession;

      return makeRequest<PatronInfoDto>({
        url: '/opac-auth/patron-info',
        method: 'POST',
        headers: {
          'X-Okapi-Token': patronSession.token,
        },
        withCredentials: true,
      })
        .then(patronInfo => {
          if (isUserValidation) {
            processPatronSession(
              patronSession.token,
              {
                user: {
                  externalSystemId: patronInfo.localPatronId[0],
                },
              },
              SSORedirectRoute
            );
          }

          setIsLoggedIn(true);

          return null;
        })
        .catch(handleValidationError);
    },
    {
      enabled: Boolean(session) && isConsortiaLogin,
    }
  );

  useQuery(
    ['guestToken', shouldGetGuestToken],
    () =>
      axios
        .get('/opac-auth/guest-token')
        .then(response => {
          const token = response.headers['x-okapi-token'];

          sessionStorage.setItem(UserSession.guest, JSON.stringify({ token }));
          setSession({ token });

          return token;
        })
        .catch(() => setIsOkapiSessionDown(true)),
    {
      enabled: shouldGetGuestToken,
    }
  );

  useEffect(() => {
    getOkapiSessionFromStorage(userSessionKey).then(okapiSession => {
      if (isGuestSession && isEmpty(okapiSession)) {
        setShouldGetGuestToken(true);
      } else {
        setSession(okapiSession);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSessionKey]);

  return {
    isLoggedIn,
    session,
    validateToken,
  };
};
